// Home (p2-home): continue where the work waits for you, a compact composer, what else waits on you, and your
// productions. First run (p2-home-empty): three ways in. Steps come from the server (GET /episodes/{id}/steps,
// data/useSteps.ts); "waiting on you" is GET /waiting (department sign-offs) plus the inbox's review requests
// and mentions.
import { type CSSProperties, type FormEvent, useEffect, useMemo, useState } from "react";
import { type Notification, notificationRoute, notificationTitle } from "../../collab/inbox.ts";
import { reviewApi } from "../../collab/reviewApi.ts";
import { creditsFromUsd } from "../../data/money.ts";
import { loadProjectForNewShot } from "../../data/newShotData.ts";
import { loadRecent, orderRecent } from "../../data/recent.ts";
import {
  type Course,
  type FilmCard,
  type Template,
  useSources,
  type WaitingItem,
} from "../../data/sources.ts";
import { doneText, heroLine, listNames, PLURAL_DEPTS, stripLine } from "../../data/steps.ts";
import { type ProductionState, useProductionStates } from "../../data/useSteps.ts";
import { CORE, DEPARTMENT_LABEL, type Department, deptColor } from "../../departments.ts";
import { aspectRatio, atLeast, projectTypeLabel } from "../../lib.ts";
import { createShotInEpisode } from "../../newShot.ts";
import { formatRoute, type Route } from "../../route.ts";
import type { Element, Project } from "../../types.ts";
import { errText, explainError, timeAgo, useApp } from "../app.ts";
import { Icon } from "../icons.tsx";
import { HiggsfieldHome } from "../import/HiggsfieldConnect.tsx";
import { ImportBanner } from "../import/ImportEntry.tsx";
import { ImportStatus } from "../import/ImportStatus.tsx";
import {
  Button,
  DeptDot,
  Dialog,
  Empty,
  LinkButton,
  ProductionCard,
  Skeleton,
  Still,
  Strip,
} from "../kit.tsx";
import { Menu } from "../Menu.tsx";
import { StatusSquare } from "../StatusSquare.tsx";
import { GettingStarted } from "./GettingStarted.tsx";
import "./home.css";

const boardRoute = (projectId: string, episodeId: string | null | undefined, dept?: Department): Route =>
  episodeId
    ? { view: "project", projectId, loc: { kind: "episode", id: episodeId, ...(dept ? { dept } : {}) } }
    : { view: "project", projectId, loc: { kind: "root" } };

export function HomeScreen({
  projects,
  loading,
  error,
  onRetry,
}: {
  projects: Project[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
}) {
  const { api, ws, meName } = useApp();
  const recent = useMemo(() => orderRecent(projects, loadRecent(), 6), [projects]);
  const states = useProductionStates(api, ws, recent);

  if (error)
    return (
      <main className="screen" id="main">
        <Empty
          title={explainError(error, "Home could not be loaded").title}
          action={<Button onClick={onRetry}>Try again</Button>}
        >
          {explainError(error).detail}
        </Empty>
      </main>
    );
  if (loading && projects.length === 0)
    return (
      <main className="screen" id="main" aria-busy="true">
        <Skeleton h={300} />
        <Skeleton h={56} className="hm-gap" />
        <Skeleton h={180} className="hm-gap" />
      </main>
    );
  if (projects.length === 0) return <FirstRun name={meName} />;

  // Continue: the most recent production with something waiting for you, else the most recent one.
  const loaded = recent
    .map((p) => ({ p, st: states.get(p.id) }))
    .filter((x): x is { p: Project; st: ProductionState } => !!x.st);
  const hero =
    loaded.find((x) => x.st.steps.some((s) => s.state === "waiting" && s.label === "Waiting for you")) ??
    loaded[0] ??
    null;

  return (
    <main className="screen hm" id="main">
      <ImportBanner />
      <ImportStatus />
      <HiggsfieldHome />
      {hero ? <Continue project={hero.p} state={hero.st} /> : <Skeleton h={300} />}
      <QuickCompose project={hero?.p ?? recent[0] ?? null} state={hero?.st ?? null} />
      <GettingStarted />
      <Waiting exclude={hero?.p.id ?? null} />
      <section className="section" aria-labelledby="hm-prods">
        <div className="section-head">
          <h2 id="hm-prods">Your productions</h2>
          <a className="link" href="#/productions">
            All productions
          </a>
        </div>
        <ProductionGrid projects={recent.slice(0, 3)} states={states} />
      </section>
    </main>
  );
}

/** The hero: the department that waits for you in your most recent production, with what signing it off opens. */
function Continue({ project, state }: { project: Project; state: ProductionState }) {
  const { api, ws } = useApp();
  const { summary, steps } = state;
  const ep = summary.latest;
  const mine = steps.find((s) => s.state === "waiting" && s.label === "Waiting for you");
  const waitingDepts = steps
    .filter((s) => s.state === "waiting" && s.label === "Waiting for you")
    .map((s) => s.dept);
  const next = steps.find((s) => s.state === "progress") ?? steps.find((s) => s.state === "open");
  const focus = mine ?? next ?? null;
  const [items, setItems] = useState<Element[]>([]);
  const subtype = focus ? SUBTYPE[focus.dept] : undefined;
  useEffect(() => {
    if (!subtype) return setItems([]);
    let live = true;
    api.listElements(ws, { projectId: project.id, subtype }).then(
      (l) => live && setItems(l.filter((e) => !e.archived_at).slice(0, 2)),
      () => live && setItems([]),
    );
    return () => {
      live = false;
    };
  }, [api, ws, project.id, subtype]);

  const label = focus ? DEPARTMENT_LABEL[focus.dept] : "";
  const headline = mine
    ? `${listNames(waitingDepts.map((d, i) => (i ? DEPARTMENT_LABEL[d].toLowerCase() : DEPARTMENT_LABEL[d])))} ${waitingDepts.length > 1 || PLURAL_DEPTS.has(waitingDepts[0]!) ? "are" : "is"} waiting for you`
    : focus
      ? `${label} ${PLURAL_DEPTS.has(focus.dept) ? "are" : "is"} next`
      : "That's a wrap";
  const finalsOpen = steps.find((s) => s.dept === "final")?.state !== "gated";
  const sub = mine
    ? `Sign off ${listNames(waitingDepts.map((d) => DEPARTMENT_LABEL[d].toLowerCase()))} to keep ${project.name} moving.`
    : finalsOpen
      ? "Everything before the finals is signed off. Finals can render."
      : // the headline names the next step; this line says what it opens, not the step again
        heroLine(steps, focus);
  const cover = summary.covers[0] ?? project.image_asset_version_id ?? null;
  // The chip names what the picture shows: with an item waiting in the focus department, the hero shows that item's
  // own picture (its current version); otherwise the production's latest picked still, without a chip.
  const lead = focus ? items[0] : undefined;
  return (
    <section className="k-card hm-hero" aria-labelledby="hm-hero-title">
      <div className="hm-hero-still">
        {lead ? (
          <Still
            src={`element:${lead.id}:${lead.current_version ?? 1}`}
            alt={`${lead.name}, v${lead.current_version ?? 1}`}
          />
        ) : (
          <Still src={cover} alt={`${project.name}: the latest picked still`} />
        )}
        {focus && lead && (
          <span className="hm-hero-tag">
            {mine ? (
              <StatusSquare state="waiting" dept={focus.dept} size={10} />
            ) : (
              <DeptDot dept={focus.dept} />
            )}{" "}
            {lead.name}
            <span className="muted">v{lead.current_version ?? 1}</span>
          </span>
        )}
      </div>
      <div className="hm-hero-text">
        <p className="hm-eyebrow">
          Continue {project.name}
          {ep ? ` · Episode ${ep.number}` : ""}
        </p>
        <h1 id="hm-hero-title">{headline}</h1>
        <p className="hm-sub">{sub}</p>
        {items.length > 0 && focus && (
          <ul className="hm-items">
            {items.map((e) => (
              <li key={e.id}>
                <a
                  href={formatRoute({ view: "element", projectId: project.id, elementId: e.id })}
                  style={{ "--dept": deptColor(focus.dept) } as CSSProperties}
                >
                  <Still
                    src={`element:${e.id}:${e.current_version ?? 1}`}
                    alt={e.name}
                    className={mine ? "hm-item-still waiting" : "hm-item-still"}
                  />
                  <span className="hm-item-name">{e.name}</span>
                  <span className="hm-item-state" style={mine ? { color: deptColor(focus.dept) } : undefined}>
                    {mine ? "Waiting for you" : focus.label}
                  </span>
                </a>
              </li>
            ))}
          </ul>
        )}
        <div className="hm-hero-actions">
          {focus && (
            <LinkButton
              variant="primary"
              size="lg"
              href={formatRoute(boardRoute(project.id, ep?.id, focus.dept))}
            >
              {mine ? `Review ${label.toLowerCase()}` : `Open ${label.toLowerCase()}`}
            </LinkButton>
          )}
          <a className="k-btn k-text k-lg" href={formatRoute(boardRoute(project.id, ep?.id))}>
            Open the board
          </a>
          <span className="grow" />
          <span className="hm-done">
            <Strip steps={steps} size={7} label={`${project.name}: production steps`} />
            {doneText(steps)}
          </span>
        </div>
      </div>
    </section>
  );
}

const SUBTYPE: Partial<Record<Department, "character" | "location" | "prop">> = {
  casting: "character",
  wardrobe: "character",
  sets: "location",
  props: "prop",
};

/** The compact composer: a new shot straight into the latest episode, opened in Create. */
function QuickCompose({ project, state }: { project: Project | null; state: ProductionState | null }) {
  const { api, ws, go } = useApp();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ep = state?.summary.latest ?? null;
  if (!project || !ep) return null;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!text.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      // the shot code comes from the episode's shots: read the production once, then create and describe it
      const d = await loadProjectForNewShot(api, ws, project, ep.id);
      const shot = await createShotInEpisode(api, ws, d, ep.id);
      await api.request("PATCH", `/workspaces/${ws}/shots/${shot.id}`, {
        body: { description: text.trim() },
      });
      go({ view: "shot", projectId: project.id, shotId: shot.id });
    } catch (err) {
      setError(errText(err));
      setBusy(false);
    }
  };
  return (
    <form className="hm-compose" onSubmit={submit} aria-label="New shot">
      <Icon name="create" size={16} />
      <label className="sr-only" htmlFor="hm-compose-input">
        Describe a shot
      </label>
      <input
        id="hm-compose-input"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Describe a shot… @ for cast and sets"
        autoComplete="off"
      />
      <span className="k-chip static hm-compose-where">
        In {project.name} · Ep {ep.number}
      </span>
      <Button type="submit" variant="outline" disabled={!text.trim() || busy}>
        {busy ? "Opening…" : "Generate"}
      </Button>
      {error && (
        <p className="error-text hm-compose-err" role="alert">
          {error}
        </p>
      )}
    </form>
  );
}

interface WaitRow {
  id: string;
  title: string;
  dept: Department | null;
  where: string;
  line: string;
  at: string;
  frame: string | null;
  route: Route | null;
  action: string;
  /** The production it belongs to, when it comes from the steps. */
  projectId: string | null;
}

/** Also waiting on you: unread sign-off requests and mentions from the inbox, and what the steps source knows. */
function Waiting({ exclude }: { exclude: string | null }) {
  const { api, ws } = useApp();
  const { steps } = useSources();
  const [all, setAll] = useState<WaitRow[] | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let live = true;
    Promise.all([
      reviewApi(api, ws)
        .inbox({ unread: true, limit: 20 })
        .then((r) => r.items)
        .catch(() => null),
      steps.waiting().catch(() => [] as WaitingItem[]),
    ]).then(([inbox, waiting]) => {
      if (!live) return;
      if (inbox === null) setFailed(true);
      const fromInbox: WaitRow[] = (inbox ?? [])
        // department sign-off requests come from GET /waiting (with the step), not twice
        .filter(
          (n: Notification) =>
            (n.kind === "review_requested" && n.subject_type !== "signoff_request") || n.kind === "mention",
        )
        .map((n) => ({
          id: n.id,
          title: notificationTitle(n),
          dept: null,
          where: "",
          line: typeof n.data.excerpt === "string" ? n.data.excerpt : "",
          at: n.created_at,
          frame: null,
          route: notificationRoute(n),
          action: n.kind === "mention" ? "Read" : "Review",
          projectId: null,
        }));
      const fromSteps: WaitRow[] = waiting.map((w) => ({
        id: w.id,
        title: `${w.versionLabel || DEPARTMENT_LABEL[w.dept]} · ${w.production}, ${w.title}`,
        dept: w.dept,
        where: DEPARTMENT_LABEL[w.dept],
        line: w.note ? `${w.line}: “${w.note}”` : w.line,
        at: w.at,
        frame: w.frame ?? null,
        route: boardRoute(w.projectId, w.episodeId, w.dept),
        action: w.kind === "read" ? "Read" : "Review",
        projectId: w.projectId,
      }));
      setAll([...fromSteps, ...fromInbox].sort((a, b) => b.at.localeCompare(a.at)));
    });
    return () => {
      live = false;
    };
  }, [api, ws, steps]);
  if (all === null) return <Skeleton h={120} className="hm-gap" />;
  // the production in the hero is shown there; which one that is can change while the others load
  const rows = all.filter((r) => !exclude || r.projectId !== exclude).slice(0, 6);
  if (rows.length === 0 && !failed) return null;
  return (
    <section className="section" aria-labelledby="hm-waiting">
      <div className="section-head">
        <h2 id="hm-waiting">
          Also waiting on you <span className="muted">· {rows.length}</span>
        </h2>
        <a className="link" href="#/signoff">
          Go through them one by one
        </a>
      </div>
      {failed && rows.length === 0 ? (
        <p className="muted">Your inbox could not be read right now.</p>
      ) : (
        <ul className="hm-wait">
          {rows.map((r) => (
            <li key={r.id}>
              <span className="hm-wait-frame" aria-hidden="true">
                {r.frame ? (
                  <Still src={r.frame} alt="" decorative />
                ) : r.dept ? (
                  <DeptDot dept={r.dept} size={10} />
                ) : (
                  <Icon name="comment" size={14} />
                )}
              </span>
              <span className="hm-wait-text">
                <span className="hm-wait-title">{r.title}</span>
                <span className="hm-wait-line">
                  {r.dept && <DeptDot dept={r.dept} />}
                  {[r.where, r.line].filter(Boolean).join(" · ")}
                </span>
              </span>
              <span className="muted hm-wait-at">{timeAgo(r.at)}</span>
              {r.route && (
                <LinkButton href={formatRoute(r.route)} size="sm">
                  {r.action}
                </LinkButton>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Production cards: cover, one meta line, the strip with a plain line, spend. */
export function ProductionGrid({
  projects,
  states,
}: {
  projects: readonly Project[];
  states: ReadonlyMap<string, ProductionState>;
}) {
  const { role } = useApp();
  const canDelete = atLeast(role, "producer");
  const [deleting, setDeleting] = useState<Project | null>(null);
  return (
    <>
      <div className="k-cards hm-cards">
        {projects.map((p) => {
          const st = states.get(p.id);
          const s = st?.summary;
          const meta = s
            ? [
                s.latest ? `Episode ${s.latest.number}` : projectTypeLabel(p.type),
                p.aspect,
                `${s.shots} ${s.shots === 1 ? "shot" : "shots"}`,
              ].join(" · ")
            : `${projectTypeLabel(p.type)} · ${p.aspect}`;
          return (
            <ProductionCard
              key={p.id}
              href={formatRoute(boardRoute(p.id, s?.latest?.id))}
              title={p.name}
              meta={meta}
              cover={s?.covers[0] ?? p.image_asset_version_id ?? null}
              clip={s?.clip ?? null}
              coverAlt={s?.covers[0] ? `${p.name}: the latest picked still` : `${p.name}: its picture`}
              steps={st?.steps ?? null}
              line={st ? stripLine(st.steps) : null}
              spentCredits={s?.spent ? creditsFromUsd(s.spent.totalUsd) : null}
              actions={
                canDelete ? (
                  <Menu
                    fixed
                    align="end"
                    buttonClass="k-prodcard-more"
                    label={`${p.name} actions`}
                    button={<Icon name="more" size={16} />}
                  >
                    {(close) => (
                      <>
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            close();
                            location.hash = formatRoute({
                              view: "project",
                              projectId: p.id,
                              loc: { kind: "settings" },
                            });
                          }}
                        >
                          Settings
                        </button>
                        <hr className="menu-sep" />
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            close();
                            setDeleting(p);
                          }}
                        >
                          Delete production…
                        </button>
                      </>
                    )}
                  </Menu>
                ) : undefined
              }
            />
          );
        })}
      </div>
      {deleting && <DeleteProduction project={deleting} onClose={() => setDeleting(null)} />}
    </>
  );
}

/** One confirm, no typing: the production leaves every list. Takes, costs and sign-offs stay on record. */
export function DeleteProduction({ project, onClose }: { project: Project; onClose: () => void }) {
  const { api, ws, onProjectChanged } = useApp();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const p = await api.request<Project>("POST", `/workspaces/${ws}/projects/${project.id}/archive`);
      onProjectChanged?.(p);
      onClose();
    } catch (x) {
      setError(errText(x));
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={`Delete ${project.name}?`}
      onClose={onClose}
      footer={
        <>
          <Button variant="text" onClick={onClose}>
            Cancel
          </Button>
          <Button className="ps-del-btn" onClick={run} disabled={busy} data-autofocus>
            {busy ? "Deleting…" : "Delete production"}
          </Button>
        </>
      }
    >
      <p className="ps-dialog-text">
        The crew loses it from every list, right away. Takes, costs and sign-offs stay on record.
      </p>
      {error && (
        <p className="cw-error" role="alert">
          The production was not deleted: {error}
        </p>
      )}
    </Dialog>
  );
}

/** First run: no productions yet. Three ways in, and the course when there is one. */
function FirstRun({ name }: { name: string | null | undefined }) {
  const { start, learn } = useSources();
  const [original, setOriginal] = useState<FilmCard | null>(null);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [course, setCourse] = useState<Course | null>(null);
  useEffect(() => {
    let live = true;
    start.original().then(
      (o) => live && setOriginal(o),
      () => undefined,
    );
    start.templates().then(
      (t) => live && setTemplates(t),
      () => undefined,
    );
    learn.course().then(
      (c) => live && setCourse(c),
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [start, learn]);
  const first = name?.split(/[\s@]/)[0];
  return (
    <main className="screen hm-first" id="main">
      <ImportBanner />
      <ImportStatus />
      <HiggsfieldHome />
      <Strip
        steps={CORE.map(({ id: dept }) => ({
          dept,
          state: "progress",
          label: "",
        }))}
        size={11}
        label="The nine departments"
      />
      <h1 className="hm-welcome">Welcome{first ? `, ${first}` : ""}. Let's take your film off the shelf.</h1>
      <p className="hm-sub">
        Pick a way in. Your film moves through nine departments: AI does the work in each one, and you and
        your crew sign off before anything expensive happens.
      </p>
      <div className="hm-doors">
        <a className="k-card hm-door" href="#/new">
          <span className="hm-door-mark">
            <Icon name="file" size={22} />
          </span>
          <span className="hm-door-title">Start from a script</span>
          <span className="hm-door-text">
            We break it into scenes, shots, cast and sets. You check the breakdown, then the crew takes it
            from there.
          </span>
          <span className="k-btn k-outline hm-door-cta">Start from a script</span>
        </a>
        {original && (
          <a className="k-card hm-door" href="#/new">
            <span className="hm-door-still">
              <Still src={original.still} alt={original.alt} />
              <span className="hm-door-tag">Original · {original.title}</span>
            </span>
            <span className="hm-door-title">Remix an Original</span>
            <span className="hm-door-text">
              Open {original.title} with every cast member, set, sign-off and cost in place. Change anything
              and make it yours.
            </span>
            <span className="hm-door-cta-row">
              <span className="k-btn k-primary hm-door-cta">
                <Icon name="remix" size={14} /> Remix {original.title}
              </span>
              <span className="k-meta">{original.shots ?? ""} shots · free to remix</span>
            </span>
          </a>
        )}
        {templates.length > 0 ? (
          <a className="k-card hm-door" href="#/new">
            <span className="hm-door-tiles">
              {templates.slice(0, 3).map((t) => (
                <span key={t.id} className="hm-door-tile">
                  {t.still ? (
                    <Still src={t.still} alt="" decorative />
                  ) : (
                    <span
                      className="hm-door-frame"
                      style={{ "--far": aspectRatio(t.aspect) } as CSSProperties}
                    />
                  )}
                  <span className="hm-door-tag">{t.name}</span>
                </span>
              ))}
            </span>
            <span className="hm-door-title">Start from a template</span>
            <span className="hm-door-text">
              {listNames(templates.map((t, i) => (i ? t.name.toLowerCase() : t.name)))}, with the departments
              and sign-offs already set up for that format.
            </span>
            <span className="k-btn k-outline hm-door-cta">See templates</span>
          </a>
        ) : (
          <a className="k-card hm-door" href="#/new">
            <span className="hm-door-drop">
              <Icon name="plus" size={22} />
              <span>An empty production</span>
            </span>
            <span className="hm-door-title">Start empty</span>
            <span className="hm-door-text">Name it, pick a format, and add episodes and shots yourself.</span>
            <span className="k-btn k-outline hm-door-cta">New production</span>
          </a>
        )}
        <a className="k-card hm-door" href="#/create">
          <span className="hm-door-drop">
            <Icon name="create" size={22} />
            <span>Describe a shot</span>
            <span className="k-meta">a still or a video, from one prompt</span>
          </span>
          <span className="hm-door-title">Make one shot</span>
          <span className="hm-door-text">
            Skip the setup. Describe a shot and get your first take. Use it in a production whenever you like.
          </span>
          <span className={`k-btn ${original ? "k-outline" : "k-primary"} hm-door-cta`}>Make a shot</span>
        </a>
      </div>
      <GettingStarted />
      {course && (
        <a className="k-card hm-course" href="#/learn">
          <Still src={course.still} alt="" decorative className="hm-course-still" />
          <span className="hm-course-text">
            <span className="hm-door-title">New here? Make your first film</span>
            <span className="hm-door-text">One short course, one lesson per department.</span>
            <span className="k-meta">Lesson 1 · {course.lessons[0]?.minutes ?? 12} min</span>
          </span>
          <span className="hm-course-cta">
            Start lesson 1 <Icon name="arrow" size={14} />
          </span>
        </a>
      )}
    </main>
  );
}
